import {useEffect, useMemo} from 'react';

import {safeLocalStorage} from '../../../../../util/localStorage';
import * as QueryTS from '../../../../../util/queryts';
import {type Sort} from '../../../../../util/queryTypes';
import {
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {normalizeNumber} from './numberNormalization';
import {useConfigValuesAggregation} from './useConfigValuesAggregation';
import {useConfigValuesCategories} from './useConfigValuesCategories';
import {useMetricStatisticsAggregation} from './useMetricStatisticsAggregation';
import {useMetricStatisticsForRun} from './useMetricStatisticsForRun';
import {useSemanticLegendSettingsWithCategoricalConfig} from './useSemanticLegendSettingsWithCategoricalConfig';
export const useSemanticLegendSettingsQueryPropsWithCategoricalConfig = ({
  createSemanticLegendSettings,
  entityName,
  filters,
  order,
  projectName,
  viewID,
  viewName,
  configKeys,
  localKey,
  localYAxisValue,
}: {
  createSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
  entityName: string;
  filters: string;
  order: Sort;
  projectName: string;

  viewID: string;
  viewName: string;
  configKeys?: string[];

  // Allow overriding the current key with the local key in case of viewing
  // someone else's workspace
  localKey?: string;
  localYAxisValue?: YAxisValues;
}) => {
  const {
    constructedSemanticLegendData,
    currentKey,
    semanticLegendSettingsError,
    semanticLegendSettingsLoading,
    shouldSkipMetricStatistics,
    shouldSkipConfigValuesAggregation,
    shouldSkipConfigValuesCategories,
  } = useSemanticLegendSettingsWithCategoricalConfig(
    viewID,
    configKeys,
    localKey,
    localYAxisValue
  );

  const {
    metricStatisticsAggregation,
    metricStatisticsError,
    metricStatisticsLoading,
  } = useMetricStatisticsAggregation({
    currentKey,
    entityName,
    projectName,
    shouldSkipMetricStatistics,
  });

  const {
    metricStatisticsForRun,
    metricStatisticsForRunError,
    metricStatisticsForRunLoading,
  } = useMetricStatisticsForRun({
    currentKey,
    entityName,
    filters,
    order: QueryTS.sortToOrderString(order),
    projectName,
    shouldSkip: shouldSkipMetricStatistics,
  });

  const {
    configValuesForRun,
    configValuesNumberAggregation,
    configValuesError,
    configValuesLoading,
  } = useConfigValuesAggregation({
    currentKey,
    entityName,
    filters,
    order,
    projectName,
    shouldSkip: shouldSkipConfigValuesAggregation,
  });

  const {
    configValuesForRun: configValuesForRunCategories,
    configValuesCategories,
    configValuesError: configValuesErrorCategories,
    configValuesLoading: configValuesLoadingCategories,
  } = useConfigValuesCategories({
    currentKey,
    entityName,
    filters,
    order,
    projectName,
    shouldSkip: shouldSkipConfigValuesCategories,
  });

  const combinedError = useMemo(
    () =>
      semanticLegendSettingsError ||
      metricStatisticsError ||
      metricStatisticsForRunError ||
      configValuesError ||
      configValuesErrorCategories,
    [
      semanticLegendSettingsError,
      metricStatisticsError,
      metricStatisticsForRunError,
      configValuesError,
      configValuesErrorCategories,
    ]
  );

  // Combine all loading states for consistent checking
  const combinedLoading = useMemo(() => {
    // Always include semantic legend settings loading
    let loading = semanticLegendSettingsLoading;

    // Only include metric loading states if metric queries should NOT be skipped
    if (!shouldSkipMetricStatistics) {
      loading =
        loading || metricStatisticsLoading || metricStatisticsForRunLoading;
    }

    // Only include config loading states if config queries should NOT be skipped
    if (!shouldSkipConfigValuesAggregation) {
      loading = loading || configValuesLoading;
    }

    if (!shouldSkipConfigValuesCategories) {
      loading = loading || configValuesLoadingCategories;
    }

    return loading;
  }, [
    semanticLegendSettingsLoading,
    shouldSkipMetricStatistics,
    metricStatisticsLoading,
    metricStatisticsForRunLoading,
    shouldSkipConfigValuesAggregation,
    configValuesLoading,
    shouldSkipConfigValuesCategories,
    configValuesLoadingCategories,
  ]);

  // Read any saved semantic legend from local storage once per view
  const savedSemanticLegendFromStorage = useMemo(() => {
    const stored = safeLocalStorage.getItem(
      `semanticLegendSettings-${viewName}`
    );
    if (!stored) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        viewID: viewID ?? '',
      } as Partial<SemanticLegendSettingsWithCategoricalConfig>;
    } catch (error) {
      // If parsing fails, remove the corrupted data and return undefined
      console.warn('Corrupted semantic legend data in localStorage:', error);
      safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
      return DEFAULT_SEMANTIC_LEGEND_SETTINGS;
    }
  }, [viewName, viewID]);

  // If we have a saved spec and we have a real viewID and not loading, create it and clear storage
  useEffect(() => {
    if (
      savedSemanticLegendFromStorage &&
      viewID &&
      !semanticLegendSettingsLoading
    ) {
      createSemanticLegendSettings(savedSemanticLegendFromStorage);
      safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
    }
  }, [
    savedSemanticLegendFromStorage,
    viewID,
    semanticLegendSettingsLoading,
    createSemanticLegendSettings,
    viewName,
  ]);

  // Retrieve the semantic legend settings
  const {
    data: semanticLegendSettingsData,
    specPreviouslyCreated,
  }: {
    data: SemanticLegendSettingsWithCategoricalConfig;
    specPreviouslyCreated: boolean;
  } = useMemo(() => {
    const base = {
      error: combinedError,
      loading: combinedLoading,
    };

    // If a valid semantic legend settings object exists, use it
    // we can use `currentMetric` to check this since we only have it when it destructures properly off a well-formed response
    if (currentKey) {
      const metricStatistics = {
        ...metricStatisticsAggregation,
        [YAxisValues.Config_Number]: configValuesNumberAggregation,
      };
      // Now that we have the metric statistics, we can set the true min for the first bucket
      // We previously set a placeholder min for the first bucket when we constructed the semantic legend data because we only store the bucket upper bounds
      const trueMin =
        metricStatistics?.[
          (constructedSemanticLegendData?.yAxisValue as Exclude<
            YAxisValues,
            YAxisValues.Config_Categories
          >) ?? YAxisValues.Min
        ]?.min ?? undefined;
      if (
        constructedSemanticLegendData?.buckets?.[0] &&
        trueMin !== undefined
      ) {
        constructedSemanticLegendData.buckets[0].bucketMin =
          normalizeNumber(trueMin);
      }
      return {
        ...base,
        data: {
          metricStatistics,
          metricStatisticsForRun,
          configValuesForRun,
          yAxisValue: localYAxisValue,
          ...constructedSemanticLegendData,
          viewID: viewID ?? '',
          allCategories: configValuesCategories,
        },
        specPreviouslyCreated: true,
      };
    }
    // if no semantic legend settings object exists, check local storage for a saved semantic legend settings object
    if (savedSemanticLegendFromStorage) {
      return {
        ...base,
        data: savedSemanticLegendFromStorage,
        specPreviouslyCreated: true,
      };
    }

    // default to the default semantic legend settings
    return {
      ...base,
      data: {
        ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
        viewID: viewID ?? '',
        allCategories: configValuesCategories,
      },
      specPreviouslyCreated: false,
    };
  }, [
    combinedError,
    combinedLoading,
    currentKey,
    savedSemanticLegendFromStorage,
    viewID,
    configValuesCategories,
    metricStatisticsAggregation,
    configValuesNumberAggregation,
    metricStatisticsForRun,
    configValuesForRun,
    constructedSemanticLegendData,
    localYAxisValue,
  ]);

  return useMemo(
    () => ({
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
      configValuesForRunCategories,
    }),
    [
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
      configValuesForRunCategories,
    ]
  );
};
